import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useAdminContext } from '../../../contexts/AdminContext';
import { listUsers, createUser, updateUserPassword, banUser, unbanUser, updateUserRole, User } from '../../../lib/admin';
import { ThemeToggle } from '../../../components/ui/theme-toggle';
import { Button } from '../../../components/ui/button';
import { IconButton } from '../../../components/ui/icon-button';
import { Skeleton, SkeletonTable } from '../../../components/ui/skeleton';

export default function AdminUsers() {
  console.log('AdminUsers - Componente montado');
  
  const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [newRole, setNewRole] = useState('user');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Form states
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    const { user, loading: authLoading } = useAuth();
    const { isAdmin, loading: adminLoading } = useAdminContext();
    const navigate = useNavigate();
    
    console.log('AdminUsers - Estado:', { authLoading, adminLoading, user: !!user, isAdmin });

  // Verificar acesso: redirecionar se não for admin (mas só depois de carregar)
  useEffect(() => {
    console.log('AdminUsers useEffect - Verificando acesso:', { authLoading, adminLoading, user: !!user, isAdmin });
    
    if (!authLoading && !adminLoading) {
      console.log('AdminUsers - Carregamento completo. Verificando permissão...');
      if (!user || !isAdmin) {
        console.log('AdminUsers - REDIRECIONANDO para dashboard');
        navigate('/dashboard', { replace: true });
      } else {
        console.log('AdminUsers - Acesso permitido!');
      }
    } else {
      console.log('AdminUsers - Ainda carregando...');
    }
  }, [authLoading, adminLoading, user, isAdmin, navigate]);

  // Carregar usuários quando tudo estiver pronto
  useEffect(() => {
    if (!authLoading && !adminLoading && isAdmin && user) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, adminLoading, isAdmin, user]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('AdminUsers - Carregando usuários...');
      const usersList = await listUsers();
      console.log('AdminUsers - Usuários carregados:', usersList.length);
      setUsers(usersList);
    } catch (err: any) {
      console.error('Erro ao carregar usuários:', err);
      setError(err.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newUserEmail.trim() || !newUserPassword.trim()) {
      setError('Preencha todos os campos');
      return;
    }

    if (newUserPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setIsSubmitting(true);
    try {
      await createUser(newUserEmail.trim(), newUserPassword);
      setSuccess('Usuário criado com sucesso!');
      setNewUserEmail('');
      setNewUserPassword('');
      setShowCreateModal(false);
      await loadUsers();
    } catch (err: any) {
      setError(err.message || 'Erro ao criar usuário');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedUser) return;

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError('Preencha todos os campos');
      return;
    }

    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateUserPassword(selectedUser.id, newPassword);
      setSuccess('Senha atualizada com sucesso!');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar senha');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleBan = async (user: User) => {
    if (!confirm(`Tem certeza que deseja ${user.isActive ? 'inativar' : 'ativar'} este usuário?`)) {
      return;
    }

    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      if (user.isActive) {
        await banUser(user.id);
        setSuccess('Usuário inativado com sucesso!');
      } else {
        await unbanUser(user.id);
        setSuccess('Usuário ativado com sucesso!');
      }
      await loadUsers();
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar status do usuário');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      await updateUserRole(selectedUser.id, newRole);
      setSuccess('Role atualizado com sucesso!');
      setShowRoleModal(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar role');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Aguardar verificação de autenticação e admin antes de mostrar conteúdo
  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Skeleton Header */}
          <div className="mb-8">
            <Skeleton variant="text" width="300px" height="32px" className="mb-2" />
            <Skeleton variant="text" width="200px" height="20px" />
          </div>
          
          {/* Skeleton Table */}
          <div className="glass-card rounded-xl shadow-lg p-6">
            <Skeleton variant="text" width="150px" height="24px" className="mb-6" />
            <SkeletonTable />
          </div>
        </div>
      </div>
    );
  }

  // Se não for admin, mostrar loading enquanto redireciona
  if (!isAdmin || !user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] flex items-center justify-center">
        <div className="text-center glass-effect p-8 rounded-2xl">
          <i className="ri-loader-4-line text-6xl text-white animate-spin"></i>
          <p className="mt-4 text-white text-lg font-medium">Redirecionando...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] transition-colors duration-300">
        <header className="bg-white dark:bg-gradient-to-r dark:from-[#1a0f2b] dark:to-[#0d0d0d] shadow-lg border-b border-gray-200 dark:border-[#8a2be2]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <Skeleton variant="circular" width={40} height={40} />
                <div className="space-y-2">
                  <Skeleton variant="text" width="200px" height="20px" />
                  <Skeleton variant="text" width="150px" height="16px" />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Skeleton variant="rectangular" width={100} height={36} className="rounded-lg" />
                <Skeleton variant="rectangular" width={80} height={36} className="rounded-lg" />
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Skeleton Actions Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <Skeleton variant="text" width="200px" height="32px" />
            <Skeleton variant="rectangular" width={150} height={44} className="rounded-lg" />
          </div>

          {/* Skeleton Table */}
          <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-lg border border-gray-200 dark:border-[#8a2be2]/30 overflow-hidden">
            <div className="p-6">
              {/* Skeleton Table Header */}
              <div className="grid grid-cols-7 gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-[#8a2be2]/30">
                <Skeleton variant="text" width="100%" height="16px" />
                <Skeleton variant="text" width="100%" height="16px" />
                <Skeleton variant="text" width="100%" height="16px" />
                <Skeleton variant="text" width="100%" height="16px" />
                <Skeleton variant="text" width="100%" height="16px" />
                <Skeleton variant="text" width="100%" height="16px" />
                <Skeleton variant="text" width="100%" height="16px" />
              </div>
              {/* Skeleton Table Rows */}
              <SkeletonTable />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] transition-colors duration-300">
      {/* Header */}
      <header className="bg-white dark:bg-gradient-to-r dark:from-[#1a0f2b] dark:to-[#0d0d0d] shadow-lg border-b border-gray-200 dark:border-[#8a2be2]/30">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 w-full">
            {/* Lado Esquerdo - Título */}
            <div className="flex items-center space-x-3">
              <IconButton
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="md"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </IconButton>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">Gerenciamento de Usuários</h1>
                <p className="text-sm text-gray-600 dark:text-[#c780ff] font-montserrat">Administre usuários do sistema</p>
              </div>
            </div>
            
            {/* Lado Direito - Ações */}
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <Button
                onClick={() => navigate('/dashboard')}
                type="secondary"
                size="small"
                className="hidden sm:flex"
                prefix={<i className="ri-arrow-left-line"></i>}
              >
                Voltar
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-500/20 backdrop-blur-sm border border-red-400/30 rounded-xl p-4">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-300 text-xl mr-3"></i>
              <p className="text-sm text-red-200 font-medium">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-500/20 backdrop-blur-sm border border-green-400/30 rounded-xl p-4">
            <div className="flex items-center">
              <i className="ri-checkbox-circle-line text-green-300 text-xl mr-3"></i>
              <p className="text-sm text-green-200 font-medium">{success}</p>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white font-ubuntu">Usuários do Sistema</h2>
          <Button
            onClick={() => setShowCreateModal(true)}
            type="primary"
            size="large"
            className="shadow-lg hover:shadow-xl"
            prefix={<i className="ri-user-add-line text-lg"></i>}
          >
            Novo Usuário
          </Button>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-lg border border-gray-200 dark:border-[#8a2be2]/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-[#0d0d0d]/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#c780ff] uppercase tracking-wider font-montserrat">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#c780ff] uppercase tracking-wider font-montserrat">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#c780ff] uppercase tracking-wider font-montserrat">Criado em</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#c780ff] uppercase tracking-wider font-montserrat">Último Acesso</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#c780ff] uppercase tracking-wider font-montserrat">Códigos Copiados</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#c780ff] uppercase tracking-wider font-montserrat">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#c780ff] uppercase tracking-wider font-montserrat">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-[#8a2be2]/30">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-[#0d0d0d]/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-[#e0e1dd] font-ubuntu">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {user.role || 'user'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-[#c780ff] font-montserrat">
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-[#c780ff] font-montserrat">
                        {user.last_sign_in_at 
                          ? new Date(user.last_sign_in_at).toLocaleDateString('pt-BR')
                          : 'Nunca'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-semibold font-montserrat ${
                          (user.codes_copied_count || 0) > 0
                            ? 'text-[#6a0dad] dark:text-[#c780ff]'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {(user.codes_copied_count || 0).toLocaleString('pt-BR')}
                        </span>
                        <i className={`ri-file-copy-line text-xs ${
                          (user.codes_copied_count || 0) > 0
                            ? 'text-[#6a0dad] dark:text-[#c780ff]'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}></i>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setNewRole(user.role || 'user');
                            setShowRoleModal(true);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                          title="Alterar role"
                        >
                          <i className="ri-user-settings-line text-lg"></i>
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setShowPasswordModal(true);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-[#6a0dad] dark:text-[#c780ff] hover:text-[#8a2be2] dark:hover:text-[#e0e1dd]"
                          title="Alterar senha"
                        >
                          <i className="ri-lock-password-line text-lg"></i>
                        </IconButton>
                        <IconButton
                          onClick={() => handleToggleBan(user)}
                          disabled={isSubmitting}
                          variant={user.isActive ? 'danger' : 'default'}
                          size="sm"
                          className={user.isActive 
                            ? 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
                            : 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300'
                          }
                          title={user.isActive ? 'Inativar' : 'Ativar'}
                        >
                          <i className={`ri-${user.isActive ? 'user-forbid' : 'user-unfollow'}-line text-lg`}></i>
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-[#8a2be2]/30">
            <div className="p-6 border-b border-gray-200 dark:border-[#8a2be2]/30">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">Novo Usuário</h2>
                <IconButton
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUserEmail('');
                    setNewUserPassword('');
                    setError('');
                  }}
                  variant="ghost"
                  size="md"
                >
                  <i className="ri-close-line text-xl"></i>
                </IconButton>
              </div>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Email
                </label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                  placeholder="usuario@exemplo.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Senha
                </label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  buttonType="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUserEmail('');
                    setNewUserPassword('');
                    setError('');
                  }}
                  type="secondary"
                  size="medium"
                >
                  Cancelar
                </Button>
                <Button
                  buttonType="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  type="primary"
                  size="medium"
                >
                  {isSubmitting ? 'Criando...' : 'Criar Usuário'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-[#8a2be2]/30">
            <div className="p-6 border-b border-gray-200 dark:border-[#8a2be2]/30">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">Alterar Senha</h2>
                <IconButton
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  variant="ghost"
                  size="md"
                >
                  <i className="ri-close-line text-xl"></i>
                </IconButton>
              </div>
              <p className="text-sm text-gray-600 dark:text-[#c780ff] mt-2 font-montserrat">
                Usuário: {selectedUser.email}
              </p>
            </div>
            
            <form onSubmit={handleUpdatePassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Nova Senha
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Confirmar Senha
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                  placeholder="Confirme a senha"
                  required
                  minLength={6}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  buttonType="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  type="secondary"
                  size="medium"
                >
                  Cancelar
                </Button>
                <Button
                  buttonType="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  type="primary"
                  size="medium"
                >
                  {isSubmitting ? 'Atualizando...' : 'Atualizar Senha'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Role Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-[#8a2be2]/30">
            <div className="p-6 border-b border-gray-200 dark:border-[#8a2be2]/30">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">Alterar Role</h2>
                <IconButton
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                    setNewRole('user');
                    setError('');
                  }}
                  variant="ghost"
                  size="md"
                >
                  <i className="ri-close-line text-xl"></i>
                </IconButton>
              </div>
              <p className="text-sm text-gray-600 dark:text-[#c780ff] mt-2 font-montserrat">
                Usuário: {selectedUser.email}
              </p>
            </div>
            
            <form onSubmit={handleUpdateRole} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Role
                </label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  buttonType="button"
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                    setNewRole('user');
                    setError('');
                  }}
                  type="secondary"
                  size="medium"
                >
                  Cancelar
                </Button>
                <Button
                  buttonType="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  type="primary"
                  size="medium"
                >
                  {isSubmitting ? 'Atualizando...' : 'Atualizar Role'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

